import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, IconButton, Typography, Alert } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';

interface VoiceRecorderProps {
  onRecordingComplete?: (audioBlob: Blob) => void;
  onRecordingDelete?: () => void;
  disabled?: boolean;
  initialAudioBlob?: Blob | null; // For pre-loading an existing audio
  onError?: (message: string) => void;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onRecordingDelete,
  disabled = false,
  initialAudioBlob = null,
  onError,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(initialAudioBlob);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlaybackRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  // Effect to handle initial audio blob
  useEffect(() => {
    setRecordedAudioBlob(initialAudioBlob);
    if (initialAudioBlob) {
      // If there's an initial blob, we need to determine its duration
      // This is a simplified approach; a more robust solution might involve metadata or a separate prop for duration
      const audio = new Audio(URL.createObjectURL(initialAudioBlob));
      audio.onloadedmetadata = () => {
        setRecordingTime(Math.floor(audio.duration));
        URL.revokeObjectURL(audio.src);
      };
    } else {
      setRecordingTime(0);
    }
  }, [initialAudioBlob]);

  // Cleanup for recording interval and audio playback
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (audioPlaybackRef.current) {
        audioPlaybackRef.current.pause();
        audioPlaybackRef.current = null;
      }
    };
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const startRecording = useCallback(async () => {
    setInternalError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      setRecordingTime(0);
      setRecordedAudioBlob(null); // Clear previous recording

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedAudioBlob(audioBlob);
        if (onRecordingComplete) {
          onRecordingComplete(audioBlob);
        }
        stream.getTracks().forEach(track => track.stop()); // Stop microphone access
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      const message = 'Не вдалося отримати доступ до мікрофона. Перевірте дозволи.';
      setInternalError(message);
      if (onError) {
        onError(message);
      }
    }
  }, [onRecordingComplete, onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  }, [isRecording]);

  const playRecording = useCallback(() => {
    if (recordedAudioBlob) {
      if (audioPlaybackRef.current) {
        audioPlaybackRef.current.pause();
        audioPlaybackRef.current = null;
      }
      const audioUrl = URL.createObjectURL(recordedAudioBlob);
      audioPlaybackRef.current = new Audio(audioUrl);
      audioPlaybackRef.current.play();
      setIsPlaying(true);
      audioPlaybackRef.current.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
      };
      audioPlaybackRef.current.onerror = (e) => {
        console.error('Audio playback error:', e);
        const message = 'Помилка відтворення аудіо.';
        setInternalError(message);
        if (onError) {
          onError(message);
        }
        setIsPlaying(false);
      };
    }
  }, [recordedAudioBlob, onError]);

  const stopPlayback = useCallback(() => {
    if (audioPlaybackRef.current) {
      audioPlaybackRef.current.pause();
      audioPlaybackRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  const deleteRecording = useCallback(() => {
    stopPlayback();
    setRecordedAudioBlob(null);
    setRecordingTime(0);
    if (onRecordingDelete) {
      onRecordingDelete();
    }
  }, [onRecordingDelete, stopPlayback]);

  return (
    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
      {internalError && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{internalError}</Alert>}

      {!recordedAudioBlob && !isRecording && (
        <Button
          variant="contained"
          startIcon={<MicIcon />}
          onClick={startRecording}
          disabled={disabled}
        >
          Записати голос
        </Button>
      )}

      {isRecording && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<StopIcon />}
          onClick={stopRecording}
          disabled={disabled}
        >
          Зупинити ({formatTime(recordingTime)})
        </Button>
      )}

      {recordedAudioBlob && !isRecording && (
        <>
          {!isPlaying ? (
            <IconButton onClick={playRecording} disabled={disabled}>
              <PlayArrowIcon />
            </IconButton>
          ) : (
            <IconButton onClick={stopPlayback} disabled={disabled}>
              <PauseIcon />
            </IconButton>
          )}
          <Typography variant="body2">{formatTime(recordingTime)}</Typography>
          <IconButton onClick={deleteRecording} disabled={disabled}>
            <DeleteIcon />
          </IconButton>
        </>
      )}
    </Box>
  );
};
